import React from 'react';
import { FlaskConical, Package, Rocket, CheckCircle2, Archive, Circle } from 'lucide-react';

/**
 * iter129 — R&D Timeline (status Gantt strip).
 * Renders the 4 forward-moving status phases plus the optional Shelved tag.
 * Past statuses show their timestamp; the current one glows; future ones are dimmed.
 *
 * Reads `project.status_history` (append-only list of {status, ts, user_name}).
 * Backend seeds this on create and backfills on legacy projects when they're fetched.
 */
const PHASES = [
  { key: 'Concept', icon: FlaskConical, label: 'Concept' },
  { key: 'Prototype', icon: Package, label: 'Prototype' },
  { key: 'Pilot', icon: Rocket, label: 'Pilot' },
  { key: 'Released', icon: CheckCircle2, label: 'Released' },
];

export default function RDTimeline({ project }) {
  if (!project) return null;
  const history = Array.isArray(project.status_history) ? project.status_history : [];
  const current = project.status;
  const shelved = current === 'Shelved';

  // First timestamp per status (chronological)
  const firstTs = {};
  for (const h of history) {
    if (h.status && !firstTs[h.status]) firstTs[h.status] = h.ts;
  }
  const phaseIndex = PHASES.findIndex(p => p.key === current);

  const fmt = (iso) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: '2-digit' }); }
    catch { return (iso || '').slice(0, 10); }
  };

  return (
    <div className="border border-slate-200 rounded-md bg-white p-4" data-testid="rd-timeline">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-slate-700">Project Timeline</h3>
        {shelved && (
          <span className="text-[10px] text-neutral-600 bg-neutral-100 border border-neutral-200 px-2 py-0.5 rounded flex items-center gap-1">
            <Archive className="w-3 h-3" /> Shelved {firstTs['Shelved'] ? `· ${fmt(firstTs['Shelved'])}` : ''}
          </span>
        )}
      </div>
      <div className="relative flex items-center justify-between">
        {/* Connector line */}
        <div className="absolute left-0 right-0 top-5 h-0.5 bg-slate-200" />
        {/* Progress line (filled up to current phase) */}
        <div
          className="absolute left-0 top-5 h-0.5 bg-fuchsia-400 transition-all"
          style={{ width: phaseIndex < 0 ? '0%' : `${(phaseIndex / (PHASES.length - 1)) * 100}%` }}
        />
        {PHASES.map((ph, i) => {
          const done = phaseIndex > i;
          const active = phaseIndex === i && !shelved;
          const ts = firstTs[ph.key];
          const Icon = ph.icon;
          return (
            <div key={ph.key} className="relative z-10 flex flex-col items-center" data-testid={`rd-timeline-phase-${ph.key}`}>
              <div
                className={
                  'w-10 h-10 rounded-full border-2 flex items-center justify-center transition-colors ' +
                  (active
                    ? 'bg-fuchsia-500 border-fuchsia-600 text-white animate-pulse shadow-md shadow-fuchsia-200'
                    : done
                      ? 'bg-fuchsia-100 border-fuchsia-400 text-fuchsia-700'
                      : 'bg-white border-slate-300 text-slate-400')
                }
                title={active ? 'Current phase' : done ? 'Completed' : 'Upcoming'}
              >
                {done ? <CheckCircle2 className="w-5 h-5" /> : <Icon className="w-4 h-4" />}
              </div>
              <div className={'mt-2 text-[11px] font-medium ' + (active ? 'text-fuchsia-700' : done ? 'text-slate-700' : 'text-slate-400')}>
                {ph.label}
              </div>
              <div className="text-[10px] text-slate-500 h-3">
                {ts ? fmt(ts) : (active ? 'now' : '')}
              </div>
            </div>
          );
        })}
      </div>
      {history.length > 1 && (
        <details className="mt-3 text-[11px] text-slate-500">
          <summary className="cursor-pointer hover:text-slate-800" data-testid="rd-timeline-history-toggle">
            Full history ({history.length} entries)
          </summary>
          <ul className="mt-2 ml-4 list-disc space-y-0.5">
            {history.map((h, i) => (
              <li key={i} data-testid={`rd-timeline-history-entry-${i}`}>
                <b className="text-slate-700">{h.status}</b> · {fmt(h.ts)}{h.user_name ? ` · ${h.user_name}` : ''}
              </li>
            ))}
          </ul>
        </details>
      )}
      {history.length === 0 && (
        <div className="mt-3 text-[11px] text-slate-400 italic flex items-center gap-1">
          <Circle className="w-3 h-3" /> No status transitions logged yet.
        </div>
      )}
    </div>
  );
}
